/**
 * PricingMatrixService — Triple-Layer Hierarchical Pricing Engine.
 *
 * Computes the display price for menu items with a zero base price
 * using a cascading waterfall:
 *
 *  LAYER 1 (Manual Pin):  Use the pinned option price if it exists and is active.
 *  LAYER 2 (Global Rule):  Apply the global pricing strategy (LOW/HIGH)
 *                          — selects a SINGLE option value from the entire
 *                          pool of available mandatory options.
 *  LAYER 3 (Fallback):    Show 'Customizable' / 'Price varies' text.
 *
 * This service is used by the Editor card view, Digital Preview, and Public Links.
 */

import type { MenuItem, OptionGroup, MenuItemOption, PricingStrategy } from '../types';

// ─── Core result type ──────────────────────────────────────────────────────────

export interface DisplayPrice {
  /** Computed price value (null when no numeric price can be determined) */
  price: number | null;
  /** Which layer resolved the price */
  layer: 'PIN' | 'STRATEGY' | 'FALLBACK' | 'BASE';
  /** Human-readable label for UI badges (e.g. "From QAR 10.00") */
  label: string | null;
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

/** Get only visible (non-hidden) options from a group */
function activeOptions(grp: OptionGroup): MenuItemOption[] {
  return grp.options.filter(o => !o.hidden);
}

/** Get mandatory groups (minSelection >= 1) that are visible and have active options */
function mandatoryGroups(item: MenuItem): OptionGroup[] {
  return (item.optionGroups || []).filter(
    g => !g.hidden && (g.minSelection ?? 0) >= 1 && activeOptions(g).length > 0,
  );
}

/** Find the pinned option across all mandatory groups (if any) */
function findActivePin(item: MenuItem): { option: MenuItemOption; group: OptionGroup } | null {
  for (const grp of mandatoryGroups(item)) {
    const pinned = activeOptions(grp).find(o => o.pinned);
    if (pinned) return { option: pinned, group: grp };
  }
  return null;
}

// ─── Strategy calculators (Direct Option Value — single selection) ─────────────

/** Collect ALL active options from ALL mandatory groups into one flat pool */
function collectMandatoryOptionPool(groups: OptionGroup[]): MenuItemOption[] {
  const pool: MenuItemOption[] = [];
  for (const grp of groups) {
    for (const opt of activeOptions(grp)) {
      pool.push(opt);
    }
  }
  return pool;
}

/** Select the single lowest-priced option from the mandatory pool */
function lowestMandatoryOption(groups: OptionGroup[]): number {
  const pool = collectMandatoryOptionPool(groups);
  if (pool.length === 0) return 0;
  return Math.min(...pool.map(o => o.extraPrice));
}

/** Select the single highest-priced option from the mandatory pool */
function highestMandatoryOption(groups: OptionGroup[]): number {
  const pool = collectMandatoryOptionPool(groups);
  if (pool.length === 0) return 0;
  return Math.max(...pool.map(o => o.extraPrice));
}

// ─── Main engine ───────────────────────────────────────────────────────────────

/**
 * Calculate display price for a menu item.
 *
 * @param item       The menu item
 * @param strategy   Global pricing strategy from settings (default: 'LOWEST')
 * @param currency   Currency symbol for label formatting
 */
export function calculateDisplayPrice(
  item: MenuItem,
  strategy: PricingStrategy = 'LOWEST',
  currency = 'QAR',
): DisplayPrice {
  // Normal priced items — use base price directly
  if (item.basePrice > 0) {
    return { price: item.basePrice, layer: 'BASE', label: null };
  }

  // Zero-price items — enter the waterfall

  // LAYER 1: Manual Pin
  const pin = findActivePin(item);
  if (pin) {
    const prefix = strategy === 'HIGHEST' ? 'Up to' : 'From';
    return {
      price: pin.option.extraPrice,
      layer: 'PIN',
      label: `${prefix} ${currency} ${pin.option.extraPrice.toFixed(2)}`,
    };
  }

  // LAYER 2: Global Strategy — select a single option value
  const mandatory = mandatoryGroups(item);
  if (mandatory.length > 0) {
    let computed: number;
    let prefix: string;
    switch (strategy) {
      case 'HIGHEST':
        computed = highestMandatoryOption(mandatory);
        prefix = 'Up to';
        break;
      case 'LOWEST':
      default:
        computed = lowestMandatoryOption(mandatory);
        prefix = 'From';
        break;
    }
    if (computed > 0) {
      return {
        price: computed,
        layer: 'STRATEGY',
        label: `${prefix} ${currency} ${computed.toFixed(2)}`,
      };
    }
  }

  // LAYER 3: Fallback
  const hasAnyGroups = (item.optionGroups || []).some(g => !g.hidden);
  return {
    price: null,
    layer: 'FALLBACK',
    label: hasAnyGroups ? 'Customizable' : 'Price varies',
  };
}

/**
 * Quick boolean: should we hide the numeric price and show a badge instead?
 */
export function shouldShowBadge(item: MenuItem): boolean {
  return item.basePrice === 0 && (item.optionGroups || []).some(g => !g.hidden);
}
